import axios from 'axios';
import type { ComboWeatherSnapshot } from './sprayService';

// Send-side weather payload. Lowercase only — matches the live operator
// flow and the plan spec. The backend persists whatever string is sent
// (the schema is plain String), but we keep the type narrow to prevent
// casing drift across the back-capture form.
export type WeatherSnapshot = {
  temperatureCategory: 'cold' | 'optimal' | 'hot';
  windCategory: 'calm' | 'breeze' | 'strong';
  precipitationCategory: 'none' | 'light' | 'heavy';
  overrideComment?: string;
};

export type BackCaptureCombo = {
  _id: string;
  tenantId: string;
  sessionId: string;
  operatorId: string;
  machineId: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'incomplete' | 'cancelled';
  startedAt?: string;
  endedAt?: string;
  // Combos returned by the list endpoint carry the full snapshot shape
  // (flat-category + finalSelected/systemReported). Use the richer
  // ComboWeatherSnapshot from sprayService for read-side parity with
  // ComboSummaryList and the audit reports.
  weatherSnapshots?: { start?: ComboWeatherSnapshot; end?: ComboWeatherSnapshot };
  ppeChecklist?: Array<{ item: string; checked: boolean; checkedAt: string | null }>;
  tankCount?: number;
  captureMode?: 'live' | 'back_capture';
  capturedBy?: string | null;
  backCaptureBeyondLimit?: boolean;
  parent: {
    _id: string;
    programId?: string;
    orchardIds?: string[];   // Backend `SpraySession.orchardIds` — array of ObjectIds serialised as strings.
    date?: string;           // Backend `SpraySession.date` — ISO timestamp.
    status?: string;
    seq?: number;            // Tenant-scoped sequence number, e.g. SES-42.
    name?: string;           // Human-readable session name.
    [k: string]: unknown;
  };
};

export type StartPayload = {
  startedAt: string;
  weatherStart: WeatherSnapshot;
  ppeChecks?: Array<{ item: string; checked: boolean }>;
};

export type CompletePayload = {
  endTime: string;
  weatherEnd: WeatherSnapshot;
  tankCount: number;
};

export type EndEarlyPayload = CompletePayload & { reason: string };

export async function listBackCaptureCombos(): Promise<BackCaptureCombo[]> {
  const { data } = await axios.get<{ combos: BackCaptureCombo[] }>(
    '/api/v1/spray/back-capture/combos'
  );
  return data.combos;
}

export async function getBackCaptureCombo(comboId: string): Promise<BackCaptureCombo> {
  const { data } = await axios.get<{ combo: BackCaptureCombo }>(
    `/api/v1/spray/back-capture/combos/${comboId}`
  );
  return data.combo;
}

export async function startBackCapture(comboId: string, payload: StartPayload) {
  const { data } = await axios.post(`/api/v1/spray/back-capture/combos/${comboId}/start`, payload);
  return data;
}

export async function completeBackCapture(comboId: string, payload: CompletePayload) {
  const { data } = await axios.post(`/api/v1/spray/back-capture/combos/${comboId}/complete`, payload);
  return data;
}

export async function endEarlyBackCapture(comboId: string, payload: EndEarlyPayload) {
  const { data } = await axios.post(`/api/v1/spray/back-capture/combos/${comboId}/end-early`, payload);
  return data;
}

export interface BackCaptureMixPreview {
  machine: { _id: string; name: string; tankVolume: number };
  intensity: string;
  totalWaterL: number;
  tankCount: number;
  perTankMix: Array<{
    productId: string;
    productName: string;
    dosePerTank: number;
    doseUnit: string;
  }>;
}

export async function getBackCaptureMixPreview(comboId: string): Promise<BackCaptureMixPreview> {
  const { data } = await axios.get<BackCaptureMixPreview>(
    `/api/v1/spray/back-capture/combos/${comboId}/mix-preview`
  );
  return data;
}
